import "./Notes.css";
import { type FormEvent, type KeyboardEvent, useEffect, useRef, useState } from "react";
import { usePortal } from "../context.js";
import { addNote, changeNote, deleteNote, errorMessage, invalidate, loadNotes, notesKey, useResource } from "../data.js";
import { hasNotes, NOTE_MAX, noteDate, notesFor, oneLine, pinnedFor, type Note } from "../model/notes.js";

/** Every note of mine, or nothing while it loads or when it couldn't be read: a pin never breaks a page. */
export function useNotes(): Note[] | undefined {
  return useResource(notesKey, loadNotes).value;
}

const pinPath = <path fill="currentColor" d="M4.2 1h3.6l-.5 3.1 1.9 1.7v1.1H6.6V11L6 11.6 5.4 11V6.9H2.8V5.8l1.9-1.7L4.2 1Z" />;

export function NoteMark() {
  return (
    <span className="note-mark" title="Has notes">
      <svg viewBox="0 0 12 12" aria-hidden="true">
        <path fill="currentColor" d="M2.5 1h5L10 3.5V10a1 1 0 0 1-1 1H2.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1Zm.2 1.2v7.6h6.1V4.2H7V2.2H2.7Zm1 3.3h4.6v1H3.7v-1Zm0 2h3.4v1H3.7v-1Z" />
      </svg>
      <span className="sr">Has notes</span>
    </span>
  );
}

/** The athlete's pinned note as a line under their name. */
export function PinnedLine({ note, full }: { note: Note | undefined; full?: boolean }) {
  if (!note) return null;
  const text = oneLine(note.body);
  return (
    <span className={"note-pinned" + (full ? " is-full" : "")} title={full ? undefined : text}>
      <svg viewBox="0 0 12 12" aria-hidden="true">
        {pinPath}
      </svg>
      <span className="sr">Pinned note: </span>
      <span className="note-pinned-text">{text}</span>
    </span>
  );
}

/** A name in a table: the note mark beside it when there are notes, the pinned one under it. */
export function NamedWithNotes({ id, name, notes }: { id: string; name: string; notes: Note[] | undefined }) {
  return (
    <>
      {name}
      {hasNotes(notes, id) ? <NoteMark /> : null}
      <PinnedLine note={pinnedFor(notes, id)} />
    </>
  );
}

function Counter({ id, length }: { id: string; length: number }) {
  return (
    <span className="note-count num" id={id}>
      {length.toLocaleString("en-US")} / {NOTE_MAX.toLocaleString("en-US")}
      <span className="sr"> characters</span>
    </span>
  );
}

const drafts = new Map<string, string>();

/** For tests. */
export function clearDrafts(): void {
  drafts.clear();
}

/** The athlete's notes on their page (DESIGN §16.8): a box to add one, then each, newest first. */
export function Notes({ athleteID, name }: { athleteID: string; name: string }) {
  const { announce } = usePortal();
  const notes = useResource(notesKey, loadNotes);
  const [draft, setDraftState] = useState(() => drafts.get(athleteID) ?? "");
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<{ id: string; text: string } | null>(null);
  const [confirming, setConfirming] = useState<string | null>(null);
  const list = useRef<HTMLUListElement>(null);
  const box = useRef<HTMLTextAreaElement>(null);
  const focusNext = useRef<string | null>(null);

  useEffect(() => {
    const target = focusNext.current;
    if (!target) return;
    focusNext.current = null;
    if (target === "box") box.current?.focus();
    else list.current?.querySelector<HTMLElement>(target)?.focus();
  });

  const setDraft = (text: string) => {
    if (text) drafts.set(athleteID, text);
    else drafts.delete(athleteID);
    setDraftState(text);
  };

  const run = (key: string, work: () => Promise<unknown>, done: string, after?: () => void) => {
    if (busy) return;
    setBusy(key);
    setError(null);
    work().then(
      () => {
        setBusy(null);
        after?.();
        announce(done);
      },
      (err: unknown) => {
        setBusy(null);
        setError(errorMessage(err));
      },
    );
  };

  const add = (e?: FormEvent) => {
    e?.preventDefault();
    const body = draft.trim();
    if (!body) return;
    run("add", () => addNote(athleteID, body), "Note added", () => {
      setDraft("");
      focusNext.current = "box";
    });
  };

  const save = (e?: FormEvent) => {
    e?.preventDefault();
    if (!editing) return;
    const body = editing.text.trim();
    if (!body) return;
    const id = editing.id;
    run(id, () => changeNote(id, { body }), "Note saved", () => {
      setEditing(null);
      focusNext.current = `[data-edit="${id}"]`;
    });
  };

  const cancelEdit = () => {
    if (!editing) return;
    focusNext.current = `[data-edit="${editing.id}"]`;
    setEditing(null);
  };

  const submitKeys = (submit: () => void, cancel?: () => void) => (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      submit();
    } else if (e.key === "Escape" && cancel) {
      e.preventDefault();
      cancel();
    }
  };

  const mine = notes.value ? notesFor(notes.value, athleteID) : [];
  const trimmed = draft.trim();
  const lockPath = <path fill="currentColor" d="M6 1a2.6 2.6 0 0 0-2.6 2.6V5H3a1 1 0 0 0-1 1v4.5a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V6a1 1 0 0 0-1-1h-.4V3.6A2.6 2.6 0 0 0 6 1Zm-1.5 4V3.6a1.5 1.5 0 0 1 3 0V5h-3Z" />;
  const shownError = notes.error ? errorMessage(notes.error) : error;
  return (
    <section className="ath-section note-section" id="ath-notes" aria-labelledby="ath-notes-h">
      <div className="ath-sec-head">
        <h2 id="ath-notes-h">Notes</h2>
        <span id="note-new-help" className="ath-sec-note note-help">
          <svg viewBox="0 0 12 12" aria-hidden="true">
            {lockPath}
          </svg>
          Only you see these
        </span>
      </div>
      <div className="note-card">
        <form className="note-new" onSubmit={add}>
          <label htmlFor="note-new-box" className="sr">
            {"A note about " + name}
          </label>
          <textarea
            id="note-new-box"
            ref={box}
            className="note-box"
            rows={2}
            maxLength={NOTE_MAX}
            value={draft}
            placeholder={"A note about " + name.split(/\s+/)[0] + ": an injury, what you talked about, what to watch…"}
            aria-describedby="note-new-help"
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={submitKeys(add)}
          />
          <div className="note-bar">
            {draft.length > NOTE_MAX - 200 ? <Counter id="note-new-count" length={draft.length} /> : <span className="note-spacer" />}
            <button type="submit" className="btn primary note-add" disabled={!trimmed || !!notes.error}>
              {busy === "add" ? "Adding…" : "Add note"}
            </button>
          </div>
        </form>
        {shownError ? (
          <p className="note-error" role="alert">
            {shownError}
            {notes.error ? (
              <button type="button" className="note-link" onClick={() => invalidate(notesKey)}>
                Try again
              </button>
            ) : null}
          </p>
        ) : null}
        {notes.error ? null : notes.loading && !notes.value ? (
          <p className="note-quiet" role="status">
            Loading your notes…
          </p>
        ) : mine.length ? (
          <ul className="note-list" ref={list} aria-label={"Notes about " + name}>
            {mine.map((n) => (
              <NoteRow
                key={n.id}
                note={n}
                busy={busy === n.id}
                editing={editing?.id === n.id ? editing.text : null}
                confirming={confirming === n.id}
                onEditText={(text) => setEditing({ id: n.id, text })}
                onEdit={() => {
                  setConfirming(null);
                  setEditing({ id: n.id, text: n.body });
                }}
                onSave={save}
                onCancelEdit={cancelEdit}
                editKeys={submitKeys(save, cancelEdit)}
                onPin={() => run(n.id, () => changeNote(n.id, { pinned: !n.pinned }), n.pinned ? "Note unpinned" : "Note pinned")}
                onDelete={() => {
                  setEditing(null);
                  setConfirming(n.id);
                  focusNext.current = `[data-cancel="${n.id}"]`;
                }}
                onCancelDelete={() => {
                  setConfirming(null);
                  focusNext.current = `[data-delete="${n.id}"]`;
                }}
                onConfirmDelete={() =>
                  run(n.id, () => deleteNote(n.id), "Note deleted", () => {
                    setConfirming(null);
                    focusNext.current = "box";
                  })
                }
              />
            ))}
          </ul>
        ) : null}
      </div>
    </section>
  );
}

function NoteRow(props: {
  note: Note;
  busy: boolean;
  editing: string | null;
  confirming: boolean;
  onEditText: (text: string) => void;
  onEdit: () => void;
  onSave: (e?: FormEvent) => void;
  onCancelEdit: () => void;
  editKeys: (e: KeyboardEvent<HTMLTextAreaElement>) => void;
  onPin: () => void;
  onDelete: () => void;
  onCancelDelete: () => void;
  onConfirmDelete: () => void;
}) {
  const { note: n, busy, editing } = props;
  const date = noteDate(n);
  const which = (
    <>
      {" "}
      <span className="sr">the note from {date}</span>
    </>
  );
  return (
    <li className={"note" + (n.pinned ? " is-pinned" : "")}>
      {editing != null ? (
        <form className="note-edit" onSubmit={props.onSave}>
          <label htmlFor={"note-edit-" + n.id} className="sr">
            Edit the note from {date}
          </label>
          <textarea
            id={"note-edit-" + n.id}
            className="note-box is-editing"
            rows={3}
            maxLength={NOTE_MAX}
            autoFocus
            value={editing}
            aria-describedby={"note-count-" + n.id}
            onChange={(e) => props.onEditText(e.target.value)}
            onKeyDown={props.editKeys}
          />
          <div className="note-bar">
            <Counter id={"note-count-" + n.id} length={editing.length} />
            <button type="button" className="btn note-btn" onClick={props.onCancelEdit}>
              Cancel
            </button>
            <button type="submit" className="btn primary note-btn" disabled={!editing.trim()}>
              {busy ? "Saving…" : "Save"}
            </button>
          </div>
        </form>
      ) : (
        <p className="note-body">{n.body}</p>
      )}
      <div className="note-foot">
        {n.pinned ? (
          <span className="note-pin-tag">
            <svg viewBox="0 0 12 12" aria-hidden="true">
              {pinPath}
            </svg>
            Pinned
          </span>
        ) : null}
        <span className="note-date num">{date}</span>
        {editing != null ? null : props.confirming ? (
          <span className="note-actions" role="group" aria-label="Delete this note?">
            <span className="note-ask">Delete this note?</span>
            <button type="button" className="note-link" data-cancel={n.id} onClick={props.onCancelDelete}>
              Cancel
            </button>
            <button type="button" className="note-link is-danger is-confirm" onClick={props.onConfirmDelete}>
              {busy ? "Deleting…" : "Delete"}
            </button>
          </span>
        ) : (
          <span className="note-actions">
            <button type="button" className="note-link" data-edit={n.id} onClick={props.onEdit}>
              Edit{which}
            </button>
            <button type="button" className="note-link" onClick={props.onPin}>
              {n.pinned ? "Unpin" : "Pin"}
              {which}
            </button>
            <button type="button" className="note-link is-danger" data-delete={n.id} onClick={props.onDelete}>
              Delete{which}
            </button>
          </span>
        )}
      </div>
    </li>
  );
}
